import { Grid, GridItem, Stack, StackProps } from '@chakra-ui/react';
import { useIsoStates } from '@pelicargo/ui';
import { COUNTRIES } from '@pelicargo/utils';
import { ChakraStylesConfig } from 'chakra-react-select';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';

type Props = StackProps & {
  name?: string;
  handleAddressChange?: () => void;
  /** Lay the fields out in two columns instead of a single stacked column. */
  columns?: 1 | 2;
};

// SimpleReactSelect renders taller than SimpleInput out of the box: the field
// keeps its own bottom padding on top of the react-select control's min-height
// and vertical padding. Drop those and let the value row alone match what an
// unstyled Input gets inside SimpleField - one md line, then 16px to the border.
const selectFieldProps = { w: 'full', p: 0, pb: 0, isRequired: true, hideLowerErrorMessage: true };
const addressFormStyles = { '& .chakra-form__required-indicator': { display: 'none' } };

const selectChakraStyles: ChakraStylesConfig = {
  control: (provided) => ({ ...provided, fontSize: 'md', minH: 'auto' }),
  valueContainer: (provided) => ({ ...provided, pt: 0, pb: 4, lineHeight: 'base' }),
  // The search input shares its grid cell with the value, so its own 2px of
  // vertical padding is what sets the row height - drop it or the row is 25px.
  inputContainer: (provided) => ({ ...provided, py: 0 }),
};

export const AddressForm = ({ name, handleAddressChange, columns = 1, ...props }: Props) => {
  const getFieldName = useCallback((field: string) => (name ? `${name}.${field}` : field), [name]);
  const { control } = useFormContext();

  const states = useIsoStates(control, getFieldName('country'));

  const recipientField = (
    <SimpleInput
      name={getFieldName('recipient')}
      label="Business name or recipient"
      variant="outline"
      onBlur={handleAddressChange}
      isRequired
      hideLowerErrorMessage
    />
  );

  const lineOneField = (
    <SimpleInput
      name={getFieldName('line_one')}
      label="Street Address"
      variant="outline"
      onBlur={handleAddressChange}
      isRequired
      hideLowerErrorMessage
    />
  );

  const lineTwoField = (
    <SimpleInput
      name={getFieldName('line_two')}
      label="Unit (Optional)"
      variant="outline"
      onBlur={handleAddressChange}
    />
  );

  const countryField = (
    <SimpleReactSelect
      name={getFieldName('country')}
      label="Country"
      variant="outline"
      options={COUNTRIES.map((country) => ({
        label: country?.name,
        value: country?.code,
      }))}
      simpleFieldProps={selectFieldProps}
      chakraStyles={selectChakraStyles}
    />
  );

  const stateField = (
    <SimpleReactSelect
      name={getFieldName('state')}
      label="State"
      variant="outline"
      options={states}
      simpleFieldProps={selectFieldProps}
      chakraStyles={selectChakraStyles}
    />
  );

  const cityField = (
    <SimpleInput
      name={getFieldName('city')}
      label="City"
      variant="outline"
      onBlur={handleAddressChange}
      w="full"
      isRequired
      hideLowerErrorMessage
    />
  );

  const zipCodeField = (
    <SimpleInput
      name={getFieldName('zip_code')}
      label="Zip Code"
      variant="outline"
      onBlur={handleAddressChange}
      w="full"
      isRequired
      hideLowerErrorMessage
    />
  );

  if (columns === 2) {
    return (
      <Grid
        templateColumns={{ base: '1fr', sm: 'repeat(12, minmax(0, 1fr))' }}
        gap="2"
        w="full"
        sx={addressFormStyles}
        {...props}
      >
        <GridItem colSpan={{ base: 1, sm: 12 }}>{recipientField}</GridItem>
        <GridItem colSpan={{ base: 1, sm: 8 }}>{lineOneField}</GridItem>
        <GridItem colSpan={{ base: 1, sm: 4 }}>{lineTwoField}</GridItem>
        <GridItem colSpan={{ base: 1, sm: 6 }}>{cityField}</GridItem>
        <GridItem colSpan={{ base: 1, sm: 3 }}>{stateField}</GridItem>
        <GridItem colSpan={{ base: 1, sm: 3 }}>{zipCodeField}</GridItem>
        <GridItem colSpan={{ base: 1, sm: 12 }}>{countryField}</GridItem>
      </Grid>
    );
  }

  return (
    <Stack sx={addressFormStyles} {...props}>
      {recipientField}
      {lineOneField}
      {lineTwoField}
      {countryField}
      {stateField}
      <Stack direction={{ base: 'column', sm: 'row' }} spacing="4" w="full">
        <Stack flex="1" minW={0} w="full">
          {cityField}
        </Stack>
        <Stack flex="1" minW={0} w="full">
          {zipCodeField}
        </Stack>
      </Stack>
    </Stack>
  );
};
